/**
 * Logs slow interactions (INP) of real visitors to the dataLayer.
 *
 * Every click, tap or key press slower than SLOW_MS pushes an `inp_slow` event
 * with the element, the section and the script that blocked the main thread,
 * so we know what to optimize. GTM sends it to GA4 as an event; when
 * Microsoft Clarity is loaded, the recording also gets the `inp_slow` tag.
 *
 * Phases of an interaction (https://web.dev/articles/inp):
 * - input delay: the main thread was busy with other work (often 3rd party),
 * - processing: our event handlers,
 * - presentation: style, layout and paint after the handlers.
 */
(function () {
  if (!window.PerformanceObserver || !PerformanceObserver.supportedEntryTypes) return;
  if (!PerformanceObserver.supportedEntryTypes.includes('event')) return;
  if (window.Shopify && window.Shopify.designMode) return;

  const SLOW_MS = 200;
  const MAX_REPORTS = 5;
  const template = document.currentScript && document.currentScript.dataset.template;

  const longFrames = [];
  const slowest = {};
  const sent = new Set();

  function describeElement(element) {
    if (!element || !element.tagName) return '';
    const parts = [];
    let node = element;
    while (node && node.tagName && parts.length < 3 && node !== document.body) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        parts.unshift(`${part}#${node.id}`);
        break;
      }
      const classes = typeof node.className === 'string' ? node.className.trim().split(/\s+/).slice(0, 2) : [];
      if (classes.length && classes[0]) part += `.${classes.join('.')}`;
      parts.unshift(part);
      node = node.parentElement;
    }
    return parts.join(' > ').slice(0, 100);
  }

  // shopify-section-template--123__main-product -> main-product
  function sectionName(element) {
    const section = element && element.closest && element.closest('.shopify-section');
    if (!section) return '';
    return section.id.replace(/^shopify-section-/, '').replace(/^.*__/, '');
  }

  function scriptName(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      return `${parsed.hostname}/${parsed.pathname.split('/').pop()}`;
    } catch (error) {
      return '';
    }
  }

  // The script that ran longest in the frames that overlap the interaction.
  function blockingScript(start, end) {
    let longest = null;
    longFrames.forEach((frame) => {
      if (frame.startTime > end || frame.startTime + frame.duration < start) return;
      (frame.scripts || []).forEach((script) => {
        if (!longest || script.duration > longest.duration) longest = script;
      });
    });
    if (!longest) return '';
    const name = scriptName(longest.sourceURL) || longest.invokerType || '';
    // Long URLs keep only the first query param, the time must always fit.
    const invoker = (longest.invoker || '').replace(/&.*$/, '');
    return `${`${name} ${invoker}`.trim().slice(0, 92)} ${Math.round(longest.duration)}ms`;
  }

  function loadState(time) {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!navigation) return '';
    if (time < navigation.domContentLoadedEventEnd) return 'loading';
    if (time < navigation.loadEventEnd || !navigation.loadEventEnd) return 'dom-ready';
    return 'complete';
  }

  function report(entry) {
    const end = entry.startTime + entry.duration;
    const data = {
      event: 'inp_slow',
      inp_value: Math.round(entry.duration),
      inp_event_type: entry.name,
      inp_target: describeElement(entry.target),
      inp_section: sectionName(entry.target),
      inp_input_delay: Math.round(entry.processingStart - entry.startTime),
      inp_processing: Math.round(entry.processingEnd - entry.processingStart),
      inp_presentation: Math.round(Math.max(0, end - entry.processingEnd)),
      inp_script: blockingScript(entry.startTime, end),
      inp_load_state: loadState(entry.startTime),
      inp_template: template || '',
      inp_pointer: window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'mouse',
    };

    // The report must not slow down the next interaction.
    const send = () => {
      (window.dataLayer = window.dataLayer || []).push(data);
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'inp_slow', `${data.inp_value}ms ${data.inp_target}`);
        window.clarity('event', 'inp_slow');
      }
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(send, { timeout: 2000 });
    } else {
      setTimeout(send, 0);
    }
  }

  if (PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')) {
    new PerformanceObserver((list) => {
      longFrames.push(...list.getEntries());
      // Only recent frames are needed for the attribution.
      if (longFrames.length > 50) longFrames.splice(0, longFrames.length - 50);
    }).observe({ type: 'long-animation-frame', buffered: true });
  }

  new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (!entry.interactionId || entry.duration < SLOW_MS) return;
      // One interaction has several entries (pointerdown, pointerup, click); keep the slowest.
      const previous = slowest[entry.interactionId];
      if (previous && previous.duration >= entry.duration) return;
      slowest[entry.interactionId] = entry;
    });

    // Long animation frame entries of the interaction arrive in the same frame, so report a bit later.
    setTimeout(() => {
      Object.keys(slowest).forEach((id) => {
        if (sent.has(id) || sent.size >= MAX_REPORTS) return;
        sent.add(id);
        report(slowest[id]);
      });
    }, 100);
  }).observe({ type: 'event', durationThreshold: SLOW_MS, buffered: true });
})();
